import { CurrentFiltersType, GoToCallbackType, SortedTermsType } from '@/hooks/use-insights';
import clsx from 'clsx';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLocalization } from '@/hooks/use-localization';

type Props = {
    basePath: string;
    sortedTerms: SortedTermsType;
    currentFilters: CurrentFiltersType;
    goTo: GoToCallbackType;
    navBar?: boolean;
};

export const Filters = ({ sortedTerms, basePath, currentFilters, goTo, navBar = false }: Props) => {
    const { localize } = useLocalization();
    const terms = useMemo(() => sortedTerms[currentFilters.group] || [], [currentFilters, sortedTerms]);

    return (
        <div data-component="filters">
            <div
                className={clsx('r-flex r-w-full r-items-center r-justify-between r-gap-3 r-bg-tertiary-500 r-p-4 r-font-medium lg:r-hidden', {
                    'r-hidden': !navBar,
                })}
            >
                <p className="r-text-base r-text-white">Resurs Insights</p>
                <div className="r-flex r-gap-2">
                    {['private', 'corporate'].map((group, index) => (
                        <Link
                            key={index}
                            className={clsx(
                                'r-rounded-xl r-px-2 r-py-1 r-text-sm',
                                currentFilters.group === group ? 'r-bg-white r-text-black' : 'r-text-white'
                            )}
                            to={`${basePath}?group=${group}`}
                            onClick={e => {
                                e.preventDefault();
                                scrollTo(0, 0);
                                goTo('group', group);
                            }}
                        >
                            {localize(group)}
                        </Link>
                    ))}
                </div>
            </div>

            {!navBar ? (
                <div className="r-flex r-flex-wrap r-items-center r-justify-between r-gap-2 r-pb-8 r-font-medium">
                    <div className="r-flex r-flex-wrap r-gap-2">
                        <Link
                            className={clsx(
                                'r-rounded-xl r-px-2 r-py-1 r-text-sm r-transition-all r-duration-200 hover:r-bg-tertiary-500 hover:r-text-white max-lg:r-border max-lg:r-border-tertiary-500',
                                {
                                    'r-bg-tertiary-500 r-text-white': !currentFilters.term && !currentFilters.currentInsight,
                                }
                            )}
                            to={`${basePath}?group=${currentFilters.group}`}
                            onClick={e => {
                                e.preventDefault();
                                goTo('group', currentFilters.group);
                            }}
                        >
                            {localize('show-all', true)}
                        </Link>
                        {terms.map((term, index) => (
                            <Link
                                key={index}
                                className={clsx(
                                    'r-rounded-xl r-px-2 r-py-1 r-text-sm r-transition-all r-duration-200 hover:r-bg-tertiary-500 hover:r-text-white max-lg:r-border max-lg:r-border-tertiary-500',
                                    {
                                        'r-bg-tertiary-500 r-text-white': currentFilters.term?.slug === term.slug,
                                    }
                                )}
                                to={`${basePath}/${term.slug}`}
                                onClick={e => {
                                    e.preventDefault();
                                    goTo('term', term.slug);
                                }}
                            >
                                {term.title}
                            </Link>
                        ))}
                    </div>
                    <div className="r-flex r-gap-2 max-lg:r-hidden">
                        {['private', 'corporate'].map((group, index) => (
                            <Link
                                key={index}
                                className={clsx(
                                    'r-rounded-xl r-px-2 r-py-1 r-text-sm r-transition-all r-duration-200 hover:r-bg-[#efe8e4] hover:r-text-black',
                                    currentFilters.group === group ? 'r-bg-[#efe8e4] r-font-semibold' : 'r-text-black'
                                )}
                                to={`${basePath}?group=${group}`}
                                onClick={e => {
                                    e.preventDefault();
                                    scrollTo(0, 0);
                                    goTo('group', group);
                                }}
                            >
                                {localize(group)}
                            </Link>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
